"use client";

import { WifiOff } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/**
 * "You are offline" — but only once we have actually failed to reach the server.
 *
 * `navigator.onLine` alone is not good enough to drive a persistent badge. It
 * reports whether the browser thinks it has *a network interface*, not whether
 * this origin is reachable, and it is famously false-positive:
 *
 *  - DevTools → Network → "Offline" throttling leaves it stuck at `false` for
 *    the whole session, which is the usual reason this badge appears on
 *    localhost while everything works.
 *  - Some VPNs and virtual interfaces report the same on macOS.
 *  - A Capacitor WebView can report `false` before the native bridge settles.
 *
 * Use `navigator.onLine` only as the *trigger* and a same-origin probe as the
 * *evidence*. The browser may not emit another `online` event when a page loads
 * in an already-disconnected state, so the probe is also repeated while the
 * origin remains unreachable.
 */

/** Cheap, always-present, same-origin. Never cached. */
const PROBE_URL = "/manifest.webmanifest";
const PROBE_TIMEOUT_MS = 5_000;
/** While we believe we are offline, re-check on this cadence. */
const RECHECK_INTERVAL_MS = 15_000;

async function originIsReachable(): Promise<boolean> {
  try {
    const response = await fetch(`${PROBE_URL}?probe=${Date.now()}`, {
      method: "HEAD",
      cache: "no-store",
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    // Any HTTP answer at all means the network reached us. A 404 would still
    // prove reachability, so the status is deliberately not checked.
    return response.type !== "error";
  } catch {
    return false;
  }
}

export function OfflineBadge() {
  const [isOffline, setIsOffline] = useState(false);
  // Guards against overlapping probes when several signals fire together (a
  // visibility change landing on top of an `offline` event, say).
  const probeInFlight = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let recheckTimer: ReturnType<typeof setInterval> | null = null;

    const probe = async () => {
      if (probeInFlight.current) return;
      probeInFlight.current = true;
      try {
        const reachable = await originIsReachable();
        if (!cancelled) setIsOffline(!reachable);
      } finally {
        probeInFlight.current = false;
      }
    };

    const evaluate = () => {
      // `onLine === true` is trustworthy in the negative direction: the browser
      // does not claim a connection it has no interface for. So skip the probe
      // and clear immediately — this is the common path and must stay free.
      if (navigator.onLine) {
        setIsOffline(false);
        return;
      }
      void probe();
    };

    evaluate();

    window.addEventListener("online", evaluate);
    window.addEventListener("offline", evaluate);
    // Coming back to a backgrounded tab is exactly when the connection state is
    // most likely to have changed without an event firing.
    document.addEventListener("visibilitychange", evaluate);

    // Only re-probe while we believe we are offline, so an online session pays
    // nothing for this.
    if (isOffline) {
      recheckTimer = setInterval(evaluate, RECHECK_INTERVAL_MS);
    }

    return () => {
      cancelled = true;
      window.removeEventListener("online", evaluate);
      window.removeEventListener("offline", evaluate);
      document.removeEventListener("visibilitychange", evaluate);
      if (recheckTimer) clearInterval(recheckTimer);
    };
  }, [isOffline]);

  if (!isOffline) return null;

  return (
    <div
      // `status`, not `alert`: losing connectivity is worth announcing, but not
      // worth interrupting whatever a screen reader is currently saying.
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed right-3 bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] z-[1000] flex max-w-[calc(100vw-1.5rem)] items-center gap-2 rounded-lg border border-warning-border bg-warning-bg px-3 py-2 text-sm text-warning shadow-lg"
    >
      <WifiOff className="size-4 shrink-0" aria-hidden="true" />
      <span className="font-medium">Offline. Reconnect for live updates.</span>
    </div>
  );
}
